import { Editor, Range } from '@tiptap/core';
import type { ViewHook } from 'phoenix_live_view';
import { type ReactNode } from 'react';

import Icon, { type IconName } from '../../Icon';
import type { SlashItem } from './slashTypes';
import type { JsonObject } from './utils';

export type FieldSuggestion = {
  type: string;
  slug: string;
  label: string;
  icon?: string;
};

export type FieldTypeSuggestion = {
  type?: string;
  label: string;
  icon?: string;
  isCategory?: boolean;
};

type PushEventTo = NonNullable<ViewHook['pushEventTo']>;
type PushEventToTarget = Parameters<PushEventTo>[0];

type BuildSlashItemsParams = {
  enableSlashCommands: boolean;
  fieldName: string;
  fieldSuggestions: FieldSuggestion[];
  fieldTypeSuggestions: FieldTypeSuggestion[];
  pushEvent?: ViewHook['pushEvent'] | undefined;
  pushEventTo?: ViewHook['pushEventTo'] | undefined;
  getEventTarget: () => PushEventToTarget | null;
  flushImmediateValue: (nextValue: string) => void;
};

const resolveIcon = (name?: string) => (name ? <Icon name={name as IconName} className='h-4 w-4' /> : null);

export const buildSlashItems = ({
  enableSlashCommands,
  fieldName,
  fieldSuggestions,
  fieldTypeSuggestions,
  pushEvent,
  pushEventTo,
  getEventTarget,
  flushImmediateValue,
}: BuildSlashItemsParams): SlashItem[] => {
  if (!enableSlashCommands) return [];

  const emitSlashEvent = (eventName: string, payload: JsonObject) => {
    const targetParam = getEventTarget();
    if (pushEventTo && targetParam) {
      pushEventTo(targetParam, eventName, payload);
      return;
    }
    pushEvent?.(eventName, payload);
  };

  const runSlashChain = (
    editor: Editor,
    range: Range,
    apply: (chain: ReturnType<Editor['chain']>) => void,
    flushWhenEmpty = false,
  ) => {
    apply(editor.chain().focus().deleteRange(range));
    if (flushWhenEmpty && editor.isEmpty) {
      flushImmediateValue('<p></p>');
    }
  };

  const makeRootActionItem = (config: {
    id: string;
    title: string;
    keywords: string[];
    group: string;
    icon: ReactNode;
    apply: (chain: ReturnType<Editor['chain']>) => void;
  }): SlashItem => ({
    id: config.id,
    title: config.title,
    keywords: config.keywords,
    group: config.group,
    scope: 'root',
    icon: config.icon,
    command: ({ editor, range }) => {
      runSlashChain(editor, range, config.apply);
    },
  });

  const headingItems: SlashItem[] = ([1, 2, 3, 4] as const).map(level =>
    makeRootActionItem({
      id: `heading-${level}`,
      title: `Heading ${level}`,
      keywords: [`h${level}`, ...(level === 1 ? ['title'] : [])],
      group: 'STRUCTURE & LAYOUT',
      icon: <span className='tiptap-heading-icon'>{`H${level}`}</span>,
      apply: chain => chain.toggleHeading({ level }).run(),
    }),
  );

  const rootItems: SlashItem[] = [
    {
      id: 'add-new-field',
      title: 'Add new field',
      keywords: ['field', 'new', 'add'],
      group: 'QUESTIONS',
      scope: 'root',
      icon: <Icon name='hero-plus' className='h-4 w-4' />,
      command: () => undefined,
    },
    ...(fieldSuggestions.length > 0
      ? [
          {
            id: 'use-existing-field',
            title: 'Use existing field',
            keywords: ['field', 'existing', 'use'],
            group: 'QUESTIONS',
            scope: 'root',
            icon: <Icon name='hero-arrow-path' className='h-4 w-4' />,
            command: () => undefined,
          } satisfies SlashItem,
        ]
      : []),
    ...headingItems,
    makeRootActionItem({
      id: 'bullet-list',
      title: 'Bullet List',
      keywords: ['list', 'bullet'],
      group: 'STRUCTURE & LAYOUT',
      icon: <Icon name='hero-list-bullet' className='h-4 w-4' />,
      apply: chain => chain.toggleBulletList().run(),
    }),
    makeRootActionItem({
      id: 'ordered-list',
      title: 'Numbered List',
      keywords: ['list', 'ordered', 'number'],
      group: 'STRUCTURE & LAYOUT',
      icon: <Icon name='hero-numbered-list' className='h-4 w-4' />,
      apply: chain => chain.toggleOrderedList().run(),
    }),
    makeRootActionItem({
      id: 'blockquote',
      title: 'Blockquote',
      keywords: ['blockquote', 'quote'],
      group: 'STRUCTURE & LAYOUT',
      icon: <Icon name='hero-chat-bubble-left-right' className='h-4 w-4' />,
      apply: chain => chain.toggleBlockquote().run(),
    }),
    makeRootActionItem({
      id: 'code-block',
      title: 'Code Block',
      keywords: ['code', 'snippet'],
      group: 'STRUCTURE & LAYOUT',
      icon: <Icon name='hero-code-bracket-square' className='h-4 w-4' />,
      apply: chain => chain.toggleCodeBlock().run(),
    }),
    makeRootActionItem({
      id: 'bold',
      title: 'Bold',
      keywords: ['bold', 'strong'],
      group: 'TEXT STYLING',
      icon: <Icon name='hero-bold' className='h-4 w-4' />,
      apply: chain => chain.toggleBold().run(),
    }),
    makeRootActionItem({
      id: 'italic',
      title: 'Italic',
      keywords: ['italic', 'em'],
      group: 'TEXT STYLING',
      icon: <Icon name='hero-italic' className='h-4 w-4' />,
      apply: chain => chain.toggleItalic().run(),
    }),
    makeRootActionItem({
      id: 'underline',
      title: 'Underline',
      keywords: ['underline'],
      group: 'TEXT STYLING',
      icon: <Icon name='hero-underline' className='h-4 w-4' />,
      apply: chain => chain.toggleUnderline().run(),
    }),
    makeRootActionItem({
      id: 'strikethrough',
      title: 'Strikethrough',
      keywords: ['strike', 'strikethrough'],
      group: 'TEXT STYLING',
      icon: <Icon name='hero-strikethrough' className='h-4 w-4' />,
      apply: chain => chain.toggleStrike().run(),
    }),
    makeRootActionItem({
      id: 'highlight',
      title: 'Highlight',
      keywords: ['highlight', 'mark'],
      group: 'TEXT STYLING',
      icon: <Icon name='hero-paint-brush' className='h-4 w-4' />,
      apply: chain => chain.toggleHighlight().run(),
    }),
    {
      id: 'image',
      title: 'Image',
      keywords: ['image', 'media', 'photo'],
      group: 'MEDIA',
      scope: 'root',
      icon: <Icon name='hero-photo' className='h-4 w-4' />,
      command: ({ editor, range }) => {
        runSlashChain(editor, range, chain => chain.run());
        emitSlashEvent('add_static_media_field', { slug: fieldName });
      },
    },
    {
      id: 'embed-link',
      title: 'Embed link',
      keywords: ['embed', 'link', 'media'],
      group: 'MEDIA',
      scope: 'root',
      icon: <Icon name='hero-link' className='h-4 w-4' />,
      command: ({ editor, range }) => {
        runSlashChain(editor, range, chain => chain.run());
        emitSlashEvent('add_static_embed_link', { slug: fieldName });
      },
    },
    {
      id: 'video',
      title: 'Video',
      keywords: ['video', 'media', 'mux'],
      group: 'MEDIA',
      scope: 'root',
      icon: <Icon name='hero-video-camera' className='h-4 w-4' />,
      command: ({ editor, range }) => {
        runSlashChain(editor, range, chain => chain.run(), true);
        emitSlashEvent('create_new_field', { type: 'video', slug: fieldName });
      },
    },
    {
      id: 'custom-block',
      title: 'Custom block',
      keywords: ['html', 'custom', 'block', 'layout'],
      group: 'MEDIA',
      scope: 'root',
      icon: <Icon name='hero-sparkles' className='h-4 w-4' />,
      command: ({ editor, range }) => {
        runSlashChain(editor, range, chain => chain.run(), true);
        emitSlashEvent('create_new_field', { type: 'custom_block', slug: fieldName });
      },
    },
  ];

  const fieldTypeItems: SlashItem[] = [];
  let currentCategory = 'Fields';

  fieldTypeSuggestions.forEach(fieldType => {
    if (fieldType.isCategory) {
      currentCategory = fieldType.label;
      return;
    }
    const fieldTypeType = fieldType.type;
    if (!fieldTypeType) return;

    fieldTypeItems.push({
      id: `field-type-${fieldTypeType}`,
      title: fieldType.label,
      keywords: ['field', 'new', fieldType.label],
      group: currentCategory,
      scope: 'new-field',
      icon: resolveIcon(fieldType.icon) ?? <Icon name='hero-plus' className='h-4 w-4' />,
      command: ({ editor, range }) => {
        runSlashChain(editor, range, chain => chain.run(), true);
        emitSlashEvent('create_new_field', { type: fieldTypeType, slug: fieldName });
      },
    });
  });

  const fieldItems: SlashItem[] = fieldSuggestions.map(field => ({
    id: `field-${field.slug}`,
    title: field.label,
    keywords: ['field', field.label],
    group: 'Fields',
    scope: 'existing-field',
    icon: resolveIcon(field.icon) ?? <Icon name='hero-at-symbol' className='h-4 w-4' />,
    command: ({ editor, range }) => {
      runSlashChain(editor, range, chain => chain.run(), true);
      emitSlashEvent('show_field', { slug: fieldName, field_slug: field.slug });
    },
  }));

  return [...rootItems, ...fieldTypeItems, ...fieldItems];
};
